import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { StatusBadge, fmtDate } from "../lib/status";
import { IncidentStatusBadge, SeverityBadge, INCIDENT_TYPE_META } from "../lib/incidents";
import {
  ArrowLeft, AlertOctagon, Plus, ClipboardList, Camera, FileText, Wallet,
  CalendarDays, History, Image as ImageIcon, Wrench, FileText as NotesIcon,
  Activity as ActivityIcon, Eye, BarChart3, Scale,
} from "lucide-react";

import { canEditVehicle } from "../components/vehicles/vehicleUtils";
import { VehicleOverviewTab } from "../components/vehicles/VehicleOverviewTab";
import { VehiclePhotosTab } from "../components/vehicles/VehiclePhotosTab";
import { VehicleBookingsTab } from "../components/vehicles/VehicleBookingsTab";
import { VehicleDocumentsTab } from "../components/vehicles/VehicleDocumentsTab";
import { VehicleInspectionsTab } from "../components/vehicles/VehicleInspectionsTab";
import { VehicleMaintenanceTab } from "../components/vehicles/VehicleMaintenanceTab";
import { VehicleMileageHistoryCard } from "../components/vehicles/VehicleMileageHistoryCard";
import { VehicleFinanceTab } from "../components/vehicles/VehicleFinanceTab";
import { VehicleNotesTab } from "../components/vehicles/VehicleNotesTab";
import { VehicleTimelineTab } from "../components/vehicles/VehicleTimelineTab";
import VehiclePerformancePanel from "../components/analytics/VehiclePerformancePanel";
import SafetyHoldPanel from "../components/safety/SafetyHoldPanel";
import VehicleQrPanel from "../components/safety/VehicleQrPanel";
import VehicleLegalPanel from "../components/client-legal/VehicleLegalPanel";
import { lrGet } from "../lib/clientLegal";

const TABS = [
  { id: "overview",   label: "Overview",      icon: ImageIcon },
  { id: "performance", label: "Performance",  icon: BarChart3 },
  { id: "photos",     label: "Photos",        icon: Camera },
  { id: "bookings",   label: "Bookings",      icon: CalendarDays },
  { id: "documents",  label: "Documents",     icon: FileText },
  { id: "legal",      label: "Legal & Safety", icon: Scale },
  { id: "inspections", label: "Inspections",  icon: History },
  { id: "maintenance", label: "Maintenance",  icon: Wrench },
  { id: "finance",    label: "Finance",       icon: Wallet },
  { id: "notes",      label: "Notes",         icon: NotesIcon },
  { id: "activity",   label: "Timeline",      icon: ActivityIcon },
];

export default function VehicleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(() => {
    const requested = searchParams.get("tab");
    return TABS.some((t) => t.id === requested) ? requested : "overview";
  });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);

  const editAllowed = canEditVehicle(user);

  const load = useCallback(async () => {
    const { data: d } = await api.get(`/vehicles/${id}`);
    setData(d);
  }, [id]);
  const loadImages = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/vehicles/${id}/photos`);
      setImages(d.items || []);
    } catch (_) { /* tolerate */ }
  }, [id]);
  const loadSummary = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/vehicles/${id}/summary`);
      setSummary(d);
    } catch (_) { /* tolerate */ }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await Promise.all([load(), loadImages(), loadSummary()]);
    })();
    return () => { cancelled = true; };
  }, [load, loadImages, loadSummary]);

  if (!data) return <div className="text-slate-500" data-testid="vehicle-detail-loading">Loading…</div>;
  const v = data.vehicle;
  const active = data.active_incidents || [];

  return (
    <div className="space-y-6 max-w-5xl" data-testid="vehicle-detail-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900" data-testid="vd-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">{v.name}</h1>
          <p className="text-sm text-slate-500 font-mono">{v.plate_number || "No plate"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={v.status} type="vehicle" />
          <Link
            to={`/app/vehicles/${v.id}/damages`}
            data-testid="vehicle-open-damages-btn"
            className="px-3 py-2 rounded-lg border border-slate-300 text-slate-800 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-2"
          >
            <ClipboardList className="w-3.5 h-3.5" />
            Damage sheet
            {data.damages_active_count > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                {data.damages_active_count}
              </span>
            )}
          </Link>
          {editAllowed && (
            <Link
              to={`/app/vehicles?edit=${v.id}&tab=public`}
              data-testid="vehicle-edit-public-btn"
              title="These details appear on your public fleet page and vehicle detail modal."
              className="px-3 py-2 rounded-lg border border-emerald-300 text-emerald-800 bg-emerald-50/60 hover:bg-emerald-100 text-sm inline-flex items-center gap-2"
            >
              <Eye className="w-3.5 h-3.5" /> Edit public details
            </Link>
          )}
          {editAllowed && (
            <Link to={`/app/incidents/new?vehicle_id=${v.id}`} data-testid="vehicle-log-incident-btn" className="px-3 py-2 rounded-lg border border-red-300 text-red-700 bg-white hover:bg-red-50 text-sm inline-flex items-center gap-2">
              <Plus className="w-3.5 h-3.5" /> Log incident
            </Link>
          )}
        </div>
      </div>
      {editAllowed && (
        <p className="text-xs text-slate-500 -mt-3" data-testid="vd-public-edit-hint">
          <em>These details appear on your public fleet page and vehicle detail modal.</em>
        </p>
      )}

      <SafetyHoldPanel vehicleId={v.id} canReport={editAllowed} onChanged={load} />
      {editAllowed && <VehicleQrPanel vehicleId={v.id} />}

      {active.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-2" data-testid="active-incidents-banner">
          {active.map((i) => (
            <div key={i.id} className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <AlertOctagon className="w-4 h-4 text-red-700" />
                  <span className="font-semibold text-red-900">{i.title}</span>
                  <SeverityBadge severity={i.severity} />
                  <IncidentStatusBadge status={i.status} />
                </div>
                <div className="text-sm text-red-900 mt-0.5">
                  {INCIDENT_TYPE_META[i.incident_type] || i.incident_type}
                  {i.estimated_resolved_datetime ? ` · Est. resolved ${fmtDate(i.estimated_resolved_datetime)}` : ""}
                </div>
              </div>
              <Link to={`/app/incidents/${i.id}`} className="text-sm font-medium text-red-900 underline hover:no-underline">View →</Link>
            </div>
          ))}
        </div>
      )}

      {/* Tab nav */}
      <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-slate-100 w-fit" data-testid="vd-tabs">
        {TABS.map((tDef) => {
          const Icon = tDef.icon;
          const active = tab === tDef.id;
          const count = tDef.id === "photos" ? data.images_count
                       : tDef.id === "bookings" ? (data.bookings?.length || 0)
                       : tDef.id === "documents" ? (data.documents?.length || 0)
                       : tDef.id === "finance" ? (data.finance_records?.length || 0)
                       : null;
          return (
            <button
              key={tDef.id}
              onClick={() => setTab(tDef.id)}
              data-testid={`vd-tab-${tDef.id}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 transition ${
                active ? "bg-white shadow-sm text-[#1B4332]" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {tDef.label}
              {count !== null && count > 0 && (
                <span className="ml-1 text-[10px] text-slate-500">({count})</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <VehicleOverviewTab v={v} summary={summary} onTabChange={setTab} />}
      {tab === "performance" && <VehiclePerformancePanel vehicleId={v.id} />}
      {tab === "photos" && (
        <VehiclePhotosTab
          vehicleId={v.id} images={images}
          editAllowed={editAllowed} uploading={uploading} setUploading={setUploading}
          onChange={async () => { await loadImages(); await load(); }}
        />
      )}
      {tab === "bookings" && <VehicleBookingsTab bookings={data.bookings || []} />}
      {tab === "documents" && <VehicleDocumentsTab vehicleId={v.id} editAllowed={editAllowed} />}
      {tab === "legal" && <LegalTab vehicleId={v.id} />}
      {tab === "inspections" && <VehicleInspectionsTab vehicleId={v.id} />}
      {tab === "maintenance" && (
        <div className="space-y-6">
          <VehicleMileageHistoryCard vehicleId={v.id} currentMileageKm={v.current_mileage_km} editAllowed={editAllowed} />
          <VehicleMaintenanceTab vehicleId={v.id} editAllowed={editAllowed} />
        </div>
      )}
      {tab === "finance" && <VehicleFinanceTab records={data.finance_records || []} locked={data.finance_locked} />}
      {tab === "notes" && <VehicleNotesTab vehicleId={v.id} editAllowed={editAllowed} />}
      {tab === "activity" && <VehicleTimelineTab vehicleId={v.id} />}
    </div>
  );
}


function LegalTab({ vehicleId }) {
  const [options, setOptions] = useState(null);
  useEffect(() => { lrGet("/options").then(setOptions).catch(() => setOptions({})); }, []);
  return <VehicleLegalPanel vehicleId={vehicleId} options={options} />;
}
